import React from 'react';
import { cn } from '@/lib/utils';
// Note: This was imported from select in original file, should be dialog.
// Correcting imports for Dialog
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  TrendingUp,
  User,
  Target,
  BarChart3,
  PieChart,
  ShieldCheck,
  History,
  UserCheck,
  MessageSquare,
} from 'lucide-react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import {
  useGetPersonalProfileUpdateQuery,
  useGetSelfReviewedSkillsQuery,
} from '@/state/skills/skills-api';
import {
  useGetIndividualProgressQuery,
  useGetProjectEvaluationReportQuery,
  useGetIndividualProgressPerEvaluationQuery,
} from '@/state/project/project-api';
import { useTenant } from '@/hooks/use-tenant';
import { parseRichText } from '@/pages/courses/components/rich-text-renderer';
import type {
  SkillRating,
  SelfReviewedSkill,
  ProgressRound,
  // ProgressPerEvaluation,
  UserEvaluationReport,
  SkillPerformance,
} from '@/types';

// Manually defining Dialog components since they might be missing in some environments or mis-imported
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

const DialogRoot = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 flex flex-col w-full max-w-5xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] border bg-background shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-2xl sm:rounded-3xl overflow-hidden',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full p-1 opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

interface UserSkillViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  projectId: number;
  evaluationSchema: string;
}

export const UserSkillViewModal = ({
  open,
  onOpenChange,
  userId,
  userName,
  projectId,
  evaluationSchema,
}: UserSkillViewModalProps) => {
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = React.useState('profile');

  // Sequential fetching based on active tab
  const { data: profileData, isLoading: loadingProfile } =
    useGetPersonalProfileUpdateQuery(
      { tenant: tenant!, userId },
      { skip: !tenant || !open || activeTab !== 'profile' }
    );

  const { data: selfReviewedData, isLoading: loadingSelf } =
    useGetSelfReviewedSkillsQuery(
      { tenant: tenant!, userId },
      { skip: !tenant || !open || activeTab !== 'self' }
    );

  const { data: progressData, isLoading: loadingProgress } =
    useGetIndividualProgressQuery(
      { tenant: tenant!, projectId, userId, schema: evaluationSchema },
      {
        skip:
          !tenant ||
          !open ||
          (activeTab !== 'progress' && activeTab !== 'history'),
      }
    );

  const { data: historyData, isLoading: loadingHistory } =
    useGetIndividualProgressPerEvaluationQuery(
      { tenant: tenant!, projectId, userId, schema: evaluationSchema },
      { skip: !tenant || !open || activeTab !== 'history' }
    );

  const { data: reportData, isLoading: loadingReport } =
    useGetProjectEvaluationReportQuery(
      { tenant: tenant!, projectId },
      { skip: !tenant || !open || activeTab !== 'report' }
    );

  const renderProfileTab = () => {
    if (loadingProfile)
      return (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      );
    if (!profileData || profileData.length === 0)
      return (
        <div className="text-center py-12 text-muted-foreground">
          No profile data found.
        </div>
      );

    const radarData = profileData.slice(0, 10).map((item: SkillRating) => ({
      skill: item?.skill_name,
      rating: parseFloat(String(item.self_evaluation)) || 0,
      interest: parseFloat(String(item.skill_interest)) || 0,
    }));

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2 text-foreground">
              <Target className="h-4 w-4 text-primary" />
              Skill Evaluation vs Interest
            </h3>
            <div className="h-[280px] sm:h-[340px] w-full text-muted-foreground">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  cx="50%"
                  cy="50%"
                  outerRadius="75%"
                  data={radarData}
                >
                  <PolarGrid stroke="currentColor" strokeOpacity={0.1} />
                  <PolarAngleAxis
                    dataKey="skill"
                    tick={{ fontSize: 9, fill: 'currentColor' }}
                  />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[0, 5]}
                    tick={{ fontSize: 9, fill: 'currentColor' }}
                  />
                  <Radar
                    name="Self Evaluation"
                    dataKey="rating"
                    stroke="#0ea5e9"
                    fill="#0ea5e9"
                    fillOpacity={0.6}
                  />
                  <Radar
                    name="Interest"
                    dataKey="interest"
                    stroke="#f43f5e"
                    fill="#f43f5e"
                    fillOpacity={0.4}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: '#fff',
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2 text-foreground">
              <BarChart3 className="h-4 w-4 text-emerald-500" />
              Skill Distribution
            </h3>
            <div className="h-[280px] sm:h-[340px] w-full text-muted-foreground">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={radarData} layout="vertical">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke="currentColor"
                    strokeOpacity={0.1}
                  />
                  <XAxis type="number" domain={[0, 5]} hide />
                  <YAxis
                    dataKey="skill"
                    type="category"
                    tick={{ fontSize: 9, fill: 'currentColor' }}
                    width={55}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: '#fff',
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Bar
                    dataKey="rating"
                    fill="#0ea5e9"
                    radius={[0, 4, 4, 0]}
                    barSize={10}
                    name="Rating"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSelfTab = () => {
    if (loadingSelf)
      return (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      );
    if (!selfReviewedData)
      return (
        <div className="text-center py-12 text-muted-foreground">
          No self-review data.
        </div>
      );

    const data = selfReviewedData
      .slice(0, 8)
      .map((item: SelfReviewedSkill) => ({
        name: item.skill?.name || `Skill ${item.id}`,
        rating: parseFloat(String(item.self_evaluation)) || 0,
        interest: parseFloat(String(item.skill_interest)) || 0,
        total: parseFloat(String(item.total_evaluated_points) || '0') || 0,
      }));

    return (
      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2 text-foreground">
          <PieChart className="h-4 w-4 text-purple-500" />
          Self Evaluation Deep Dive
        </h3>
        <div className="h-[320px] sm:h-[380px] w-full text-muted-foreground">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="currentColor"
                strokeOpacity={0.1}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9, fill: 'currentColor' }}
              />
              <YAxis
                domain={[0, 5]}
                tick={{ fontSize: 9, fill: 'currentColor' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(30, 41, 59, 0.95)',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: '#fff',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              />
              <Legend />
              <Bar
                dataKey="rating"
                fill="#0ea5e9"
                radius={[4, 4, 0, 0]}
                name="Self Evaluation"
              />
              <Bar
                dataKey="interest"
                fill="#f43f5e"
                radius={[4, 4, 0, 0]}
                name="Interest"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderProgressTab = () => {
    if (loadingProgress)
      return (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      );
    if (!progressData)
      return (
        <div className="text-center py-12 text-muted-foreground">
          No progress data.
        </div>
      );

    // Flatten data for line chart
    const lineData = progressData.map((round: ProgressRound) => {
      const entry: Record<string, string | number> = {
        round: `Round ${round.evaluation_number}`,
      };
      round.skills.forEach((s) => {
        entry[s.user_skills__skill__name] = s.evaluation_points;
      });
      return entry;
    });

    const skillNames = Array.from(
      new Set(
        progressData.flatMap((r) =>
          r.skills.map((s) => s.user_skills__skill__name)
        )
      )
    );
    const colors = [
      '#0ea5e9',
      '#f43f5e',
      '#10b981',
      '#f59e0b',
      '#6366f1',
      '#ec4899',
    ];

    return (
      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2 text-foreground">
          <TrendingUp className="h-4 w-4 text-blue-500" />
          Performance Over Evaluation Rounds
        </h3>
        <div className="h-[320px] sm:h-[380px] w-full text-muted-foreground">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={lineData}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="currentColor"
                strokeOpacity={0.1}
              />
              <XAxis
                dataKey="round"
                tick={{ fontSize: 9, fill: 'currentColor' }}
              />
              <YAxis
                domain={[0, 5]}
                tick={{ fontSize: 9, fill: 'currentColor' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(30, 41, 59, 0.95)',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: '#fff',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              />
              <Legend />
              {skillNames.map((name, idx) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={colors[idx % colors.length]}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderReportTab = () => {
    if (loadingReport)
      return (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      );
    if (!reportData || !reportData[0])
      return (
        <div className="text-center py-12 text-muted-foreground">
          No report data.
        </div>
      );

    const userData = reportData[0].find(
      (u: UserEvaluationReport) =>
        u.user.toLowerCase().includes(userName.toLowerCase().split(' ')[0]) ||
        u.user.includes(userId.substring(0, 8))
    );
    if (!userData)
      return (
        <div className="text-center py-12 text-muted-foreground">
          User not found in report.
        </div>
      );

    const gpaData = userData.skills.map((s: SkillPerformance) => ({
      name: s.name,
      gpa: s.gpa,
    }));

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center text-primary">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                Average Performance
              </p>
              <h4 className="text-2xl font-black text-primary">
                {userData.average_performance.toFixed(2)}
              </h4>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            Skill GPA Report
          </h3>
          <div className="h-[300px] sm:h-[340px] w-full text-muted-foreground">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gpaData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="currentColor"
                  strokeOpacity={0.1}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 9, fill: 'currentColor' }}
                />
                <YAxis
                  domain={[0, 5]}
                  tick={{ fontSize: 9, fill: 'currentColor' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    color: '#fff',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                />
                <Bar
                  dataKey="gpa"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  barSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };
  const renderHistoryTab = () => {
    if (loadingHistory)
      return (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      );
    if (!historyData || historyData.length === 0)
      return (
        <div className="text-center py-12 text-muted-foreground">
          No evaluation history found.
        </div>
      );

    // Sort by evaluation number descending (latest first)
    const sortedHistory = [...historyData].sort(
      (a, b) => b.evaluation_number - a.evaluation_number
    );

    return (
      <div className="space-y-6 pb-6">
        {sortedHistory.map((round) => (
          <div
            key={round.id}
            className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm"
          >
            {/* Round header */}
            <div className="bg-muted px-4 sm:px-6 py-4 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xs flex-shrink-0">
                  {round.evaluation_number}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground">
                    Evaluation Round {round.evaluation_number}
                  </h4>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(
                      round.evaluation_trigger_date
                    ).toLocaleDateString()}{' '}
                    at{' '}
                    {new Date(
                      round.evaluation_trigger_date
                    ).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="text-[10px] uppercase tracking-wider font-bold"
                >
                  {round.evaluation_trigger_by_schema}
                </Badge>
                <History className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>
            {/* Skills list */}
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-4">
                {round.skill_evaluation.map((skill, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-muted/50 border border-border hover:bg-muted transition-colors"
                  >
                    {/* Skill name + GPA badge */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h5 className="text-sm font-semibold text-foreground leading-snug flex-1">
                        {skill.skill_name}
                      </h5>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[10px] text-muted-foreground font-medium uppercase">
                          GPA
                        </span>
                        <Badge
                          className={cn(
                            'text-xs font-black px-2 text-white',
                            skill.total_gpa >= 4
                              ? 'bg-emerald-500'
                              : skill.total_gpa >= 3
                                ? 'bg-blue-500'
                                : skill.total_gpa > 0
                                  ? 'bg-orange-500'
                                  : 'bg-muted-foreground'
                          )}
                        >
                          {skill.total_gpa.toFixed(1)}
                        </Badge>
                      </div>
                    </div>
                    {/* Skill description */}
                    <div className="text-xs text-muted-foreground leading-relaxed mb-3 [&_p]:mb-0.5">
                      {parseRichText(skill.skill_description)}
                    </div>

                    {/* Detailed feedback from individual-progress cross-reference */}
                    {progressData
                      ?.find(
                        (p) => p.evaluation_number === round.evaluation_number
                      )
                      ?.skills.filter(
                        (s) => s.user_skills__skill__name === skill.skill_name
                      )
                      .map((detail, dIdx) => (
                        <div
                          key={dIdx}
                          className="mt-3 pt-3 border-t border-border flex flex-col gap-2"
                        >
                          {detail.feedback && (
                            <div className="flex items-start gap-2 bg-background p-3 rounded-lg border border-border">
                              <MessageSquare className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                              <div className="text-[11px] text-foreground leading-relaxed [&_p]:mb-0.5">
                                {parseRichText(detail.feedback)}
                              </div>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                            <UserCheck className="h-3 w-3" />
                            <span>
                              Reviewed by: {detail.user_evaluated_by_email}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>User Skill Visualization: {userName}</DialogTitle>
        </DialogHeader>
        {/* Sticky header - never scrolls */}
        <div className="flex-shrink-0 bg-background border-b border-border/40 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4 pr-8">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white shadow-lg flex-shrink-0">
              <User className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-foreground truncate">
                  {userName}
                </h2>
                <Badge
                  variant="outline"
                  className="bg-primary/5 text-primary border-primary/20 rounded-full px-3 py-0.5 text-[10px] font-bold tracking-wider uppercase flex-shrink-0"
                >
                  Member Profile
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3 flex-shrink-0" />
                Skill analysis and project progress
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            <Tabs
              defaultValue="profile"
              className="w-full"
              onValueChange={setActiveTab}
            >
              {/* Horizontally scrollable tab list on mobile */}
              <div className="overflow-x-auto pb-0 -mb-px">
                <TabsList className="flex w-max min-w-full justify-start bg-transparent border-b border-border/40 rounded-none h-auto p-0 mb-6 gap-4 sm:gap-8">
                  <TabsTrigger
                    value="profile"
                    className="flex-shrink-0 bg-transparent border-b-2 border-transparent rounded-t-lg px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-muted/20 data-[state=active]:shadow-none font-semibold text-xs sm:text-sm text-muted-foreground data-[state=active]:text-primary transition-all hover:text-foreground hover:bg-muted/50 whitespace-nowrap"
                  >
                    Personal Profile
                  </TabsTrigger>
                  <TabsTrigger
                    value="self"
                    className="flex-shrink-0 bg-transparent border-b-2 border-transparent rounded-t-lg px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-muted/20 data-[state=active]:shadow-none font-semibold text-xs sm:text-sm text-muted-foreground data-[state=active]:text-primary transition-all hover:text-foreground hover:bg-muted/50 whitespace-nowrap"
                  >
                    Self Reviewed
                  </TabsTrigger>
                  <TabsTrigger
                    value="progress"
                    className="flex-shrink-0 bg-transparent border-b-2 border-transparent rounded-t-lg px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-muted/20 data-[state=active]:shadow-none font-semibold text-xs sm:text-sm text-muted-foreground data-[state=active]:text-primary transition-all hover:text-foreground hover:bg-muted/50 whitespace-nowrap"
                  >
                    Individual Progress
                  </TabsTrigger>
                  <TabsTrigger
                    value="report"
                    className="flex-shrink-0 bg-transparent border-b-2 border-transparent rounded-t-lg px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-muted/20 data-[state=active]:shadow-none font-semibold text-xs sm:text-sm text-muted-foreground data-[state=active]:text-primary transition-all hover:text-foreground hover:bg-muted/50 whitespace-nowrap"
                  >
                    Evaluation Report
                  </TabsTrigger>
                  <TabsTrigger
                    value="history"
                    className="flex-shrink-0 bg-transparent border-b-2 border-transparent rounded-t-lg px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-muted/20 data-[state=active]:shadow-none font-semibold text-xs sm:text-sm text-muted-foreground data-[state=active]:text-primary transition-all hover:text-foreground hover:bg-muted/50 whitespace-nowrap"
                  >
                    Eval History
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent
                value="profile"
                className="mt-0 ring-offset-background focus-visible:outline-none"
              >
                {renderProfileTab()}
              </TabsContent>
              <TabsContent
                value="self"
                className="mt-0 ring-offset-background focus-visible:outline-none"
              >
                {renderSelfTab()}
              </TabsContent>
              <TabsContent
                value="progress"
                className="mt-0 ring-offset-background focus-visible:outline-none"
              >
                {renderProgressTab()}
              </TabsContent>
              <TabsContent
                value="report"
                className="mt-0 ring-offset-background focus-visible:outline-none"
              >
                {renderReportTab()}
              </TabsContent>
              <TabsContent
                value="history"
                className="mt-0 ring-offset-background focus-visible:outline-none"
              >
                {renderHistoryTab()}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </DialogRoot>
  );
};
